import React, { useEffect, useRef, useState } from "react";
import DateTime from "react-datetime";
import moment from "moment";
export default function YearOnYearMonthOnMonthSelector({
  mode = "month",
  value,
  onChange,
  placeholder = "Select range",
  disabled = false,
}) {
  const granularity = mode === "yoy" || mode === "year" ? "year" : "month";
  const unit = granularity; // "year" | "month" — also the moment.js unit name

  const [open, setOpen] = useState(false);
  const [start, setStart] = useState(value?.[0] ? moment(value[0]) : null);
  const [end, setEnd] = useState(value?.[1] ? moment(value[1]) : null);
  // true while the user has picked a start but not yet an end
  const [selectingEnd, setSelectingEnd] = useState(false);

  const wrapperRef = useRef(null);

  useEffect(() => {
    setStart(value?.[0] ? moment(value[0]) : null);
    setEnd(value?.[1] ? moment(value[1]) : null);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Click 1 sets the start of the range. Click 2 sets the end (swapping if
  // it lands before the start). Click 3 starts a brand-new range.
  const handlePick = (date) => {
    const picked = moment(date).startOf(unit);

    if (!selectingEnd) {
      setStart(picked);
      setEnd(null);
      setSelectingEnd(true);
      return;
    }

    let nextStart = start;
    let nextEnd = picked;
    if (nextEnd.isBefore(nextStart, unit)) {
      [nextStart, nextEnd] = [nextEnd, nextStart];
    }

    setStart(nextStart);
    setEnd(nextEnd);
    setSelectingEnd(false);
    onChange?.([nextStart, nextEnd]);
    setOpen(false);
  };

  const clearRange = () => {
    setStart(null);
    setEnd(null);
    setSelectingEnd(false);
  };

  const dateFormat = granularity === "year" ? "YYYY" : "MMM YYYY";
  const viewMode = granularity === "year" ? "years" : "months";

  const displayValue = () => {
    if (!start || !end) return "";
    return `${start.format(dateFormat)}  \u2192  ${end.format(dateFormat)}`;
  };

  const cellStateClass = (cellMoment) => {
    if (!start) return "";
    const isStart = cellMoment.isSame(start, unit);
    const isEnd = end && cellMoment.isSame(end, unit);
    const inRange =
      start &&
      end &&
      cellMoment.isAfter(start, unit) &&
      cellMoment.isBefore(end, unit);

    if (isStart && isEnd) return "yrp-cell--single";
    if (isStart) return "yrp-cell--start";
    if (isEnd) return "yrp-cell--end";
    if (inRange) return "yrp-cell--in-range";
    return "";
  };

  const renderYear = (props, year) => {
    const cellMoment = moment()
      .year(year)
      .startOf("year");
    const extra = cellStateClass(cellMoment);
    return (
      <td {...props} className={`${props.className} ${extra}`.trim()}>
        {year}
      </td>
    );
  };

  const renderMonth = (props, month, year) => {
    const cellMoment = moment()
      .year(year)
      .month(month)
      .startOf("month");
    const extra = cellStateClass(cellMoment);
    return (
      <td {...props} className={`${props.className} ${extra}`.trim()}>
        {moment.monthsShort(month)}
      </td>
    );
  };

  const renderProps = granularity === "year" ? { renderYear } : { renderMonth };

  return (
    <div className="yrp-root" ref={wrapperRef}>
      <div className={`yrp-input ${disabled ? "yrp-input--disabled" : ""}`}>
        <svg
          className="yrp-input__icon"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
        >
          <rect
            x="3"
            y="5"
            width="18"
            height="16"
            rx="2"
            stroke="currentColor"
            strokeWidth="1.6"
          />
          <path d="M3 9.5H21" stroke="currentColor" strokeWidth="1.6" />
          <path
            d="M8 3V6.5M16 3V6.5"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
        <input
          readOnly
          disabled={disabled}
          className="yrp-input__field"
          value={displayValue()}
          placeholder={placeholder}
          onClick={() => !disabled && setOpen((o) => !o)}
        />
        {
          <button
            type="button"
            className="yrp-input__clear"
            aria-label="Clear range"
            onClick={(e) => {
              e.stopPropagation();
              clearRange();
            }}
          >
            &times;
          </button>
        }
      </div>

      {open && (
        <div className="yrp-panel">
          {/* <DateTime
            value={selectingEnd ? start : end || start || undefined}
            open
            input={false}
            timeFormat={false}
            dateFormat={dateFormat}
            viewMode={viewMode}
            closeOnSelect={false}
            onChange={handlePick}
            {...renderProps}
          /> */}

          <DateTime
            initialViewDate={start || end || moment()}
            open
            input={false}
            timeFormat={false}
            dateFormat={dateFormat}
            viewMode={viewMode}
            closeOnSelect={false}
            onChange={handlePick}
            {...renderProps}
          />

          <div className="yrp-panel__footer">
            <span className="yrp-panel__hint">
              {!start
                ? `Select a start ${unit}`
                : selectingEnd
                ? `Select an end ${unit}`
                : ""}
            </span>
            <button
              type="button"
              className="yrp-btn yrp-btn--ghost"
              onClick={clearRange}
            >
              Clear
            </button>
          </div>
        </div>
      )}

      <style>{`
        .yrp-root {
          --yrp-primary: #2463ae;
          --yrp-blue-subtle: #7eb6f7;
          --yrp-dark-blue: #0a4a95;

          position: relative;
          font-family: inherit;
          width: 100%;
          max-width: 400px;
        }

        .yrp-input {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #fff;
          border: 1px solid #d7dce2;
          border-radius: 8px;
          padding: 8px 10px;
          cursor: pointer;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }
        .yrp-input:hover {
          border-color: var(--yrp-blue-subtle);
        }
        .yrp-input:focus-within {
          border-color: var(--yrp-primary);
          box-shadow: 0 0 0 3px rgba(36, 99, 174, 0.14);
        }
        .yrp-input--disabled {
          background: #f4f5f7;
          cursor: not-allowed;
          opacity: 0.7;
        }
        .yrp-input__icon {
          flex: none;
          color: #6b7280;
        }
        .yrp-input__field {
          border: none;
          outline: none;
          flex: 1;
          font-size: 14px;
          color: #1f2430;
          background: transparent;
          cursor: pointer;
          min-width: 0;
        }
        .yrp-input__field::placeholder {
          color: #9aa2b1;
        }
        .yrp-input__clear {
          border: none;
          background: #eef0f3;
          color: #5b6470;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          font-size: 14px;
          line-height: 1;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex: none;
        }
        .yrp-input__clear:hover {
          color: #fff;
        }

        .yrp-panel {
          position: absolute;
          top: calc(100% + 8px);
          left: 0;
          z-index: 1000;
          width: max-content;
        }

        .yrp-panel__footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          margin-top: 6px;
          padding: 0 4px;
        }

        .yrp-panel__hint {
          font-size: 12px;
          color: #8a93a3;
        }

        .yrp-btn {
          border: none;
          background: transparent;
          border-radius: 7px;
          padding: 4px 10px;
          font-size: 13px;
          font-weight: 600;
          color: var(--yrp-primary);
          cursor: pointer;
          transition: background 0.15s ease;
        }
        .yrp-btn:hover {
          background: rgba(36, 99, 174, 0.08);
        }

        /* Recolor react-datetime's default calendar with the brand
           palette, without rebuilding its layout/chrome. */
        .yrp-panel .rdtPicker {
          position: static; /* this component owns positioning, not rdt */
        }
        .yrp-panel .rdtSwitch:hover,
        .yrp-panel .rdtPrev:hover,
        .yrp-panel .rdtNext:hover {
          background: rgba(36, 99, 174, 0.08);
          color: var(--yrp-dark-blue);
        }
        .yrp-panel .rdtMonths td.rdtMonth:hover,
        .yrp-panel .rdtYears td.rdtYear:hover {
          background: var(--yrp-blue-subtle);
          color: #fff;
        }
        .yrp-panel td.rdtActive,
        .yrp-panel td.rdtActive:hover {
          background: transparent;
          text-shadow: none;
        }

        .yrp-panel td.yrp-cell--start,
        .yrp-panel td.yrp-cell--end,
        .yrp-panel td.yrp-cell--single {
          background: var(--yrp-primary) !important;
          color: #fff !important;
          font-weight: 700;
        }
        .yrp-panel td.yrp-cell--in-range {
          background: rgba(126, 182, 247, 0.3) !important;
          color: #0a4a95;
        }
      `}</style>
    </div>
  );
}
